import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Calendar from 'react-calendar';
import toast from 'react-hot-toast';
import { appointmentAPI, depositAPI } from '../services/api';
import { formatPhoneDisplay } from '../utils/phone';
import BookingForm from './BookingForm';
import { Skeleton } from './Skeleton';
import 'react-calendar/dist/Calendar.css';
import './AppointmentCalendar.css';

// Calendar tiles show the chosen service; if none was picked, fall back to the appointment notes.
const getServiceLabel = (apt) => apt?.serviceId?.name || apt?.notes?.trim() || 'No service';

export default function AppointmentCalendar({ refreshKey = 0, focusAppointmentId = null, focusDate = null }) {
  const [date, setDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('day'); // 'day' | 'week' | 'month'
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showSlotActions, setShowSlotActions] = useState(false);
  const [slotAppointments, setSlotAppointments] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const currentHourRef = useRef(null);
  const focusRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  // Close date picker when clicking outside (mouse or touch)
  useEffect(() => {
    const handler = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Drag-and-drop state
  const [dragApt, setDragApt] = useState(null); // { apt, fromTime, fromDateStr }
  const [dragOver, setDragOver] = useState(null); // timeLabel being hovered
  const [rescheduleConfirm, setRescheduleConfirm] = useState(null); // { apt, newTime, newDateStr }
  const [rescheduling, setRescheduling] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Button-based reschedule state
  const [rescheduleModal, setRescheduleModal] = useState(null); // appointment being rescheduled
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');

  const timeOptions = useMemo(() => {
    const times = [];
    for (let h = 7; h <= 20; h++) {
      for (const m of ['00', '15', '30', '45']) {
        const label = `${String(h).padStart(2, '0')}:${m}`;
        times.push(label);
      }
    }
    return times;
  }, []);

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Deep-link from a push notification: jump to the appointment's date.
  useEffect(() => {
    if (focusDate) {
      const [y, m, d] = String(focusDate).split('-').map(Number);
      if (y && m && d) setDate(new Date(y, m - 1, d));
    }
  }, [focusDate]);

  // Once appointments load, scroll the focused appointment into view.
  useEffect(() => {
    if (!focusAppointmentId) return;
    const t = setTimeout(() => {
      focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
    return () => clearTimeout(t);
  }, [focusAppointmentId, appointments, viewMode]);

  useEffect(() => {
    // Auto-scroll to current hour when switching to day view
    if (viewMode === 'day' && currentHourRef.current) {
      setTimeout(() => {
        currentHourRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    }
  }, [viewMode]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentAPI.getAll();
      setAppointments(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 5) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    const hours = Math.floor(diff / 3600);
    return `${hours}h ago`;
  };

  const formatISODate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const openBookingAtTime = (timeLabel) => {
    setSelectedTime(timeLabel);
    setShowSlotActions(false);
    setRescheduleConfirm(null);
    setShowBookingForm(true);
  };

  // Drag handlers
  const handleDragStart = (e, apt) => {
    setDragApt({ apt, fromTime: apt.startTime, fromDateStr: apt.appointmentDateKey || formatISODate(apt.appointmentDate) });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDragApt(null);
    setDragOver(null);
  };

  const handleDragOverSlot = (e, timeLabel) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(timeLabel);
  };

  const handleDropOnSlot = (e, timeLabel, targetDateStr) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragApt) return;
    if (dragApt.fromTime === timeLabel && dragApt.fromDateStr === targetDateStr) {
      setDragApt(null);
      return;
    }
    setRescheduleConfirm({ apt: dragApt.apt, newTime: timeLabel, newDateStr: targetDateStr });
    setDragApt(null);
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleConfirm) return;
    setRescheduling(true);
    try {
      const { apt, newTime, newDateStr } = rescheduleConfirm;
      await appointmentAPI.reschedule(apt._id, newDateStr, newTime);
      await fetchAppointments();
      setRescheduleConfirm(null);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to reschedule appointment');
    } finally {
      setRescheduling(false);
    }
  };

  // Button-based reschedule
  const handleOpenReschedule = (apt) => {
    setShowSlotActions(false);
    setRescheduleModal(apt);
    setRescheduleDate(new Date(apt.appointmentDate || Date.now()));
    setRescheduleTime(apt.startTime || '');
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleModal || !rescheduleDate) return;
    setRescheduling(true);
    try {
      const newDateStr = formatISODate(rescheduleDate);
      await appointmentAPI.reschedule(rescheduleModal._id, newDateStr, rescheduleTime);
      await fetchAppointments();
      setRescheduleModal(null);
      toast.success('Appointment rescheduled successfully');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to reschedule appointment');
    } finally {
      setRescheduling(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      await appointmentAPI.cancel(appointmentId);
      await fetchAppointments();
      setSlotAppointments((prev) => prev.filter((apt) => apt._id !== appointmentId));
      toast.success('Appointment cancelled');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to cancel appointment');
    }
  };

  const handleCheckIn = async (apt) => {
    if (!window.confirm(`Check in ${apt.clientName}?`)) return;
    try {
      await appointmentAPI.checkIn(apt._id);
      await fetchAppointments();
      setSlotAppointments((prev) =>
        prev.map((a) => (a._id === apt._id ? { ...a, status: 'completed' } : a))
      );
      toast.success(`${apt.clientName} checked in.`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to check in');
    }
  };

  const handleDepositStatus = async (apt, status) => {
    const labels = { paid: 'mark as PAID', refunded: 'REFUND', forfeited: 'FORFEIT' };
    if (!window.confirm(`Mark ${apt.clientName}'s deposit as ${labels[status] || status}?`)) return;
    try {
      await depositAPI.setStatus(apt._id, status);
      await fetchAppointments();
      setSlotAppointments((prev) =>
        prev.map((a) =>
          a._id === apt._id
            ? { ...a, deposit: { ...(a.deposit || {}), status } }
            : a
        )
      );
      toast.success(`Deposit marked ${status}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to update deposit');
    }
  };

  const getDepositLabel = (apt) => {
    const dep = apt?.deposit || {};
    if (!dep.amount || dep.amount <= 0) return null;
    const statusLabels = {
      none: 'No deposit',
      required: 'Required',
      pending: 'Pending',
      paid: 'Paid',
      refunded: 'Refunded',
      forfeited: 'Forfeited',
    };
    return {
      amount: `$${dep.amount}`,
      status: statusLabels[dep.status] || dep.status || 'Required',
      statusKey: dep.status || 'required',
    };
  };

  const appointmentsByDate = useMemo(() => {
    return appointments
      .filter((apt) => apt.status !== 'cancelled')
      .reduce((acc, apt) => {
      const key = apt.appointmentDateKey || formatISODate(apt.appointmentDate);
      if (!key) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(apt);
      return acc;
      }, {});
  }, [appointments]);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    setShowDatePicker(false);
    if (viewMode === 'month') setViewMode('day');
  };

  const getAppointmentsForDate = (d) => {
    const dateStr = formatISODate(d);
    return appointmentsByDate[dateStr] || [];
  };

  const getTileContent = ({ date: tileDate }) => {
    const dayAppointments = getAppointmentsForDate(tileDate);
    if (dayAppointments.length === 0) return null;
    return (
      <div className="appointment-tile-content">
        <span className="appointment-count">{dayAppointments.length}</span>
        {dayAppointments.length <= 2 && (
          <div className="tile-appt-names">
            {dayAppointments.slice(0, 2).map((apt) => (
              <span key={apt._id} className="tile-appt-name">{apt.clientName}</span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Week view: compute week start (Sunday) and list days
  const getWeekDates = (centerDate) => {
    const dt = new Date(centerDate);
    const day = dt.getDay();
    const sunday = new Date(dt);
    sunday.setDate(dt.getDate() - day);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      days.push(d);
    }
    return days;
  };

  // Day view: hourly rows (7:00 - 20:00)
  const hours = Array.from({ length: 14 }, (_, i) => 7 + i); // 7..20

  const renderModal = (content) => createPortal(content, document.body);

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h2>Appointment Calendar</h2>
        {lastUpdated && (
          <span className="last-updated" title="Click to refresh" onClick={fetchAppointments}>
            Updated {formatTimeAgo(lastUpdated)}
          </span>
        )}
      </div>

      {showBookingForm
        ? renderModal(
            <div className="booking-modal">
              <div className="booking-modal-content">
                <button className="close-modal" aria-label="Close" onClick={() => setShowBookingForm(false)}>×</button>
                <BookingForm 
                  onAppointmentCreated={() => {
                    setShowBookingForm(false);
                    fetchAppointments();
                  }}
                  prefillData={{ appointmentDate: formatISODate(date), startTime: selectedTime }}
                />
              </div>
            </div>
          )
        : showSlotActions
          ? renderModal(
              <div className="booking-modal">
                <div className="booking-modal-content slot-action-modal">
                  <button className="close-modal" aria-label="Close" onClick={() => setShowSlotActions(false)}>×</button>
                  <h3>{selectedTime} Options</h3>
                  {slotAppointments.length > 0 && (
                    <div className="slot-existing-list">
                      {slotAppointments.map((apt) => (
                        <div key={apt._id} className="slot-existing-item">
                          <div className="slot-existing-info">
                            <div className="appt-client">{apt.clientName}</div>
                            {apt.clientPhone && (
                              <div className="appt-phone">
                                <span className="appt-phone-icon">📞</span>
                                {formatPhoneDisplay(apt.clientPhone)}
                              </div>
                            )}
                            <div className="appt-service">
                              <span className="appt-label">Service:</span> {getServiceLabel(apt)}
                            </div>
                            {apt.nailTechId?.name && (
                              <div className="appt-tech">
                                <span className="appt-label">Tech:</span> {apt.nailTechId.name}
                              </div>
                            )}
                            {(() => {
                              const info = getDepositLabel(apt);
                              if (!info) return null;
                              return (
                                <div className={`appt-deposit deposit-${info.statusKey}`}>
                                  <span className="appt-label">Deposit:</span> {info.amount} ·{' '}
                                  {info.status}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="slot-existing-actions">
                            {(() => {
                              const info = getDepositLabel(apt);
                              if (!info) return null;
                              return (
                                <div className="deposit-actions">
                                  {info.statusKey !== 'paid' && info.statusKey !== 'none' && (
                                    <button
                                      className="slot-action-btn success"
                                      onClick={() => handleDepositStatus(apt, 'paid')}
                                      title="Record deposit as paid (offline payment)"
                                    >
                                      Mark Paid
                                    </button>
                                  )}
                                  {info.statusKey === 'paid' && (
                                    <>
                                      <button
                                        className="slot-action-btn"
                                        onClick={() => handleDepositStatus(apt, 'refunded')}
                                      >
                                        Refund
                                      </button>
                                      <button
                                        className="slot-action-btn"
                                        onClick={() => handleDepositStatus(apt, 'forfeited')}
                                      >
                                        Forfeit
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                            {apt.status !== 'completed' && (
                              <>
                                <button
                                  className="slot-action-btn success"
                                  onClick={() => handleCheckIn(apt)}
                                >
                                  Check-in
                                </button>
                                <button
                                  className="slot-action-btn"
                                  onClick={() => handleOpenReschedule(apt)}
                                >
                                  Re-schedule
                                </button>
                              </>
                            )}
                            <button
                              className="slot-action-btn danger"
                              onClick={() => handleCancelAppointment(apt._id)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="slot-action-row">
                    <button className="slot-action-btn primary" onClick={() => openBookingAtTime(selectedTime)}>
                      Make New Appointment
                    </button>
                  </div>
                </div>
              </div>
            )
          : rescheduleConfirm
            ? renderModal(
                <div className="booking-modal">
                  <div className="booking-modal-content slot-action-modal">
                    <button className="close-modal" aria-label="Close" onClick={() => setRescheduleConfirm(null)}>×</button>
                    <h3>Reschedule Appointment</h3>
                    <p>Move <strong>{rescheduleConfirm.apt.clientName}</strong> from <strong>{rescheduleConfirm.apt.startTime}</strong> to <strong>{rescheduleConfirm.newTime}</strong> on <strong>{rescheduleConfirm.newDateStr}</strong>?</p>
                    <p style={{ fontSize: '0.85rem', color: '#64748b' }}>An SMS will be sent to the customer confirming the new time.</p>
                    <div className="slot-action-row">
                      <button className="slot-action-btn primary" onClick={handleRescheduleConfirm} disabled={rescheduling}>
                        {rescheduling ? 'Rescheduling…' : 'Confirm Reschedule'}
                      </button>
                      <button className="slot-action-btn" onClick={() => setRescheduleConfirm(null)} disabled={rescheduling}>Cancel</button>
                    </div>
                  </div>
                </div>
              )
            : rescheduleModal
              ? renderModal(
                  <div className="booking-modal">
                    <div className="booking-modal-content slot-action-modal reschedule-modal">
                      <button className="close-modal" aria-label="Close" onClick={() => setRescheduleModal(null)}>×</button>
                      <h3>Re-schedule: {rescheduleModal.clientName}</h3>
                      <p className="reschedule-current">
                        Currently: <strong>{rescheduleModal.startTime}</strong> on <strong>{new Date(rescheduleModal.appointmentDate).toDateString()}</strong>
                      </p>
                      <div className="reschedule-picker">
                        <div className="reschedule-field">
                          <label>New Date</label>
                          <Calendar
                            onChange={(d) => setRescheduleDate(d)}
                            value={rescheduleDate}
                            minDate={new Date()}
                          />
                        </div>
                        <div className="reschedule-field">
                          <label>New Time</label>
                          <select
                            value={rescheduleTime}
                            onChange={(e) => setRescheduleTime(e.target.value)}
                            className="reschedule-time-select"
                          >
                            <option value="">Select time…</option>
                            {timeOptions.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="slot-action-row">
                        <button
                          className="slot-action-btn primary"
                          onClick={handleRescheduleSubmit}
                          disabled={rescheduling || !rescheduleDate || !rescheduleTime}
                        >
                          {rescheduling ? 'Rescheduling…' : 'Confirm Reschedule'}
                        </button>
                        <button className="slot-action-btn" onClick={() => setRescheduleModal(null)} disabled={rescheduling}>Cancel</button>
                      </div>
                    </div>
                  </div>
                )
              : null}

      <div className="calendar-controls">
        <div className="view-buttons">
          <button className={viewMode === 'day' ? 'active' : ''} onClick={() => setViewMode('day')}>Day</button>
          <button className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>Week</button>
          <button className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>Month</button>
        </div>
        <div className="date-picker" ref={datePickerRef}>
          {viewMode === 'month' ? (
            <Calendar onChange={handleDateChange} value={date} tileContent={getTileContent} />
          ) : (
            <>
              <button
                className="date-picker-btn"
                onClick={() => setShowDatePicker((v) => !v)}
                aria-label="Pick a date"
              >
                <span>📅</span>
                <span>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </button>
              {showDatePicker && (
                <div className="date-picker-popup">
                  <Calendar onChange={handleDateChange} value={date} tileContent={getTileContent} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '20px 0' }}>
          <Skeleton.Block width="40%" height={24} style={{ marginBottom: 18 }} />
          <Skeleton.Table rows={6} />
        </div>
      ) : (
        <>
          {viewMode === 'month' && (
            <div className="month-view-note">
              <p>Click a date in the calendar or use the date picker to navigate.</p>
            </div>
          )}

          {viewMode === 'week' && (
            <div className="week-view">
              {getWeekDates(date).map((d) => {
                const dayApts = getAppointmentsForDate(d);
                const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
                const dayDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={d.toISOString()} className="week-day">
                    <div className="week-day-header">
                      <div className="day-name">{dayName}</div>
                      <div className="day-date">{dayDate}</div>
                    </div>
                    <div className="week-day-appointments">
                      {dayApts.length > 0 ? (
                        dayApts.map((apt) => (
                          <div key={apt._id} className="week-appointment">
                            <div className="week-appt-time">{apt.startTime}</div>
                            <div className="week-appt-client">{apt.clientName}</div>
                            {apt.clientPhone && (
                              <div className="week-appt-phone">📞 {formatPhoneDisplay(apt.clientPhone)}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-apt">—</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {viewMode === 'day' && (
            <div className="day-view">
              <h3>Appointments for {new Date(date).toDateString()}</h3>
              <div className="day-grid">
                {hours.map((h) => {
                  const hourLabel = `${h < 10 ? '0' + h : h}:00`;
                  const quarters = ['00', '15', '30', '45'];
                  const currentHour = new Date().getHours();
                  const isCurrentHour = h === currentHour;
                  
                  return (
                    <div 
                      key={h} 
                      className="hour-block"
                      ref={isCurrentHour ? currentHourRef : null}
                    >
                      <div className="hour-header">{hourLabel}</div>
                      <div className="quarter-slots">
                        {quarters.map((q) => {
                          const timeLabel = `${h < 10 ? '0' + h : h}:${q}`;
                          const currentDateStr = formatISODate(date);
                          const rowApts = getAppointmentsForDate(date).filter((apt) => {
                            const st = apt.startTime || '';
                            return st === timeLabel;
                          });
                          const isDragTarget = dragApt && dragOver === timeLabel;
                          return (
                            <div
                              key={timeLabel}
                              className={`quarter-row${isDragTarget ? ' drag-over' : ''}`}
                              onDragOver={(e) => handleDragOverSlot(e, timeLabel)}
                              onDragLeave={() => setDragOver(null)}
                              onDrop={(e) => handleDropOnSlot(e, timeLabel, currentDateStr)}
                            >
                              <div className="time-label">{timeLabel}</div>
                              <div
                                className={`time-slots${rowApts.length > 0 ? ' has-appointments' : ''}`}
                                onClick={() => {
                                  if (dragApt) return; // ignore click during drag
                                  if (rowApts.length > 0) {
                                    setSelectedTime(timeLabel);
                                    setSlotAppointments(rowApts);
                                    setShowSlotActions(true);
                                  } else {
                                    openBookingAtTime(timeLabel);
                                  }
                                }}
                              >
                                {rowApts.length > 0 ? (
                                  rowApts.map((apt) => (
                                    <div
                                      key={apt._id}
                                      ref={apt._id === focusAppointmentId ? focusRef : undefined}
                                      className={`appointment-block draggable${apt._id === focusAppointmentId ? ' focus' : ''}`}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, apt)}
                                      onDragEnd={handleDragEnd}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTime(timeLabel);
                                        setSlotAppointments(rowApts);
                                        setShowSlotActions(true);
                                      }}
                                      title="Drag to reschedule"
                                    >
                                      <div className="appt-client">{apt.clientName}</div>
                                      <div className="appt-service">{getServiceLabel(apt)}</div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="empty-slot">{dragApt ? 'Drop here' : 'Click to make appointment'}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {viewMode === 'month' && (
            <div className="appointments-list">
              <h3>{date.toDateString()}</h3>
              {getAppointmentsForDate(date).length > 0 ? (
                <div className="month-appointments">
                  {getAppointmentsForDate(date).map((apt) => (
                    <div key={apt._id} className="month-appointment-item">
                      <div className="month-appt-time">{apt.startTime}</div>
                      <div className="month-appt-details">
                        <div className="month-appt-client">{apt.clientName}</div>
                        {apt.clientPhone && (
                          <div className="month-appt-phone">📞 {formatPhoneDisplay(apt.clientPhone)}</div>
                        )}
                        <div className="month-appt-info">
                          {apt.nailTechId?.name && <span>{apt.nailTechId.name}</span>}
                          {getServiceLabel(apt) !== 'No service' && <span> • {getServiceLabel(apt)}</span>}
                        </div>
                      </div>
                      <div className={`month-appt-status ${apt.status}`}>{apt.status}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-appointments">No appointments</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
